<script lang="ts" setup generic="T extends BeamShapingGroupDto">
import { api } from '@/api';
import {
  BeamShapingGroupDto,
  EnumerationValueDto,
  GetBeamShapingGroupDtoTypeEnum,
  instanceOfBeamShapingGroupCircularDto,
  instanceOfBeamShapingGroupRectangularDto,
  TaskTypeDescription,
} from '@/api/generator';
import PtwDeleteButton from '@/components/buttons/PtwDeleteButton.vue';

import EnumerationValue from '@/components/forms/dataTypes/EnumerationValue.vue';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import { I18nKey } from '@/utils/typings.ts';
import BeamShapingGroupCircular from '@/views/tasks/components/beamShaping/BeamShapingGroupCircular.vue';
import BeamShapingGroupRectangular from '@/views/tasks/components/beamShaping/BeamShapingGroupRectangular.vue';
import { useLatestApiRequest } from '@/views/jobOccurrences/utils/useLatestApiRequest';

import { useFieldError, useFieldValue, useSetFieldValue } from 'vee-validate';
import { computed, ref, toRef } from 'vue';

import { useTranslation } from 'i18next-vue';
const { t } = useTranslation();

const props = defineProps<{
  name: string; // taskItemArea[{areaIndex}].medicalSystemSetup.beamShapingContainer.selectedBeamShapings[{beamShapingGroupIndex}]
  taskType: TaskTypeDescription;
  canBeDeleted: boolean;
}>();

const emits = defineEmits<{
  remove: [item: BeamShapingGroupDto];
  blur: [event?: FocusEvent];
  switchShape: [];
}>();

const mode = useFormMode();

const item = useFieldValue<T>(toRef(() => props.name));

const setValue = useSetFieldValue<T>(toRef(() => props.name));
const beamShapingTemplateRequest = useLatestApiRequest({
  request: api.taskItemsApi.getTaskItemBeamShapingTemplate.bind(api.taskItemsApi),
});

// The shape select is deliberately NOT bound to the form. A half-switched row (new shape id, old
// `$type` and old shape-specific fields) must never exist in the form values, because the form-wide
// validation serialises them and sends them to the backend. The select shows the pending choice
// locally and the form only receives the complete template once it has arrived.
const pendingShape = ref<EnumerationValueDto>();
const shownShape = computed(() => pendingShape.value ?? item.value?.radiationFieldShape);

const shapeError = useFieldError(toRef(() => `${props.name}.radiationFieldShape`));
const shapeValueError = useFieldError(toRef(() => `${props.name}.radiationFieldShape.value`));
const shapeValueIdError = useFieldError(toRef(() => `${props.name}.radiationFieldShape.value.id`));

async function switchBeamShape(shape: EnumerationValueDto | undefined) {
  if (shape?.value?.id == null) {
    return;
  }

  pendingShape.value = shape;

  const { result } = await beamShapingTemplateRequest.call({
    getBeamShapingGroupDto: {
      taskType: props.taskType,
      radiationFieldShapeId: shape.value.id,
      $type:
        GetBeamShapingGroupDtoTypeEnum.PtwPtw100AppZeroApplicationSharedTaskItemComponentDtoGetBeamShapingGroupDtoPtwPtw100AppZeroApplicationShared,
    },
  });

  pendingShape.value = undefined;

  if (result) {
    setValue(result as T);
    emits('switchShape');
  }
}
</script>

<template>
  <tr class="v-data-table__tr">
    <td class="shape">
      <EnumerationValue
        v-if="shownShape"
        :enumerationValue="shownShape"
        :errorMessage="shapeError || shapeValueIdError || shapeValueError"
        :disabled="!!pendingShape"
        :items="
          item?.radiationFieldShape?.options?.map((option) => {
            return taskType === 'BeamConstancyCheck'
              ? {
                  title: `${t(option.name as I18nKey)}`,
                  value: option,
                  props: {
                    disabled: option.name !== ('EnumerationOptionLabelEnum.Rectangular' as I18nKey),
                  },
                }
              : {
                  title: `${t(option.name as I18nKey)}`,
                  value: option,
                };
          })
        "
        :mode="taskType === 'BeamConstancyCheck' ? 'details' : mode"
        @update:enumerationValue="switchBeamShape"
      />
    </td>

    <BeamShapingGroupRectangular
      v-if="instanceOfBeamShapingGroupRectangularDto(item)"
      :disabled="
        typeof item?.radiationFieldShape?.value === 'undefined' ||
        item?.radiationFieldSymmetry?.value?.name === 'EnumerationOptionLabelEnum.Symmetrical'
      "
      :name="name"
      :taskType="taskType"
      @blur="emits('blur', $event)"
    />

    <BeamShapingGroupCircular
      v-if="instanceOfBeamShapingGroupCircularDto(item)"
      :name="name"
      :taskType="taskType"
      @blur="emits('blur', $event)"
    />

    <td class="remove">
      <PtwDeleteButton v-if="mode !== 'details' && canBeDeleted" @delete="emits('remove', item)" />
    </td>
  </tr>
</template>

<style lang="scss" scoped>
.table-row {
  display: flex;
  justify-content: space-around;
}

tr {
  &.v-data-table__tr {
    outline: 2px solid transparent;
    outline-offset: -2px;
  }
}

td {
  &.shape {
    width: 15% !important;
  }

  &.remove {
    width: 56px !important;
    padding-left: 8px !important;
    vertical-align: middle !important;
  }
}
</style>
